import { Divider, Heading, Stack, Text, useToast } from '@chakra-ui/react';
import { CargoType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { useMemo } from 'react';

import {
  ProviderQuotingSection,
  ProviderQuotingValues,
  useProviderQuotingForm,
} from '../../hooks/emails/useProviderQuotingForm';
import { useCurrentRequest } from '../../hooks/useCurrentRequest';
import { useEmail } from '../../hooks/useEmail';
import { ContactTags } from '../../utils/constants';
import { sendEmailsToValidProviders } from '../../utils/email';
import { EmailDrawer } from '../Emails/EmailDrawer';
import { EmailDrawerLeftPanel } from '../Emails/EmailDrawerLeftPanel';
import { EmailDrawerRightPanel } from '../Emails/EmailDrawerRightPanel';
import { SimpleSharedEmailForm } from '../Emails/SimpleSharedEmailForm';
import { useRequestDrawersContext } from '../RequestDrawers';
import { SimpleAirlineRecipientForm } from '../SimpleAirlineRecipientForm';
import { SimpleFieldArray } from '../SimpleFieldArray';
import { SimpleForm } from '../SimpleForm';
import { SimpleTruckingRecipientForm } from '../SimpleTruckingRecipientForm';
import { SimpleWarehouseRecipientForm } from '../SimpleWarehouseRecipientForm';

enum ProviderType {
  Airline = 'airlines',
  Trucking = 'trucks',
  Warehouse = 'warehouses',
}

export const ProviderQuotingDrawer = () => {
  const toast = useToast();
  const { request, quotes } = useCurrentRequest();
  const { sendAirlineQuotingEmail, sendTruckingQuotingEmail, sendWarehouseQuotingEmail } = useEmail();
  const { methods } = useProviderQuotingForm(request, quotes);

  const hasAirlines = methods.watch('airlines.providers')?.length > 0;
  const hasTruckings = methods.watch('trucks.providers')?.length > 0;
  const hasWarehouses = methods.watch('warehouses.providers')?.length > 0;

  const { getIsModalOpen, closeModal } = useRequestDrawersContext();
  const isOpen = getIsModalOpen('ProviderQuoting');
  const onClose = () => closeModal('ProviderQuoting');

  const sendSingleEmail =
    (type: ProviderType) =>
    async (provider: ProviderQuotingValues['providers'][0], data: Omit<ProviderQuotingValues, 'providers'>) => {
      if (!provider?.isExpanded || !provider?.recipients?.length) return;
      const { id: requestId } = request;
      const { quoteId, recipients } = provider;
      const [toContact, ...ccContacts] = recipients;
      const { senderContactId, ...overrides } = data;

      if (type === ProviderType.Airline) {
        await sendAirlineQuotingEmail({
          requestId,
          quoteId,
          senderContactId,
          toContact,
          ccContacts,
          overrides,
        });
      } else if (type === ProviderType.Trucking) {
        await sendTruckingQuotingEmail({
          requestId,
          quoteId,
          senderContactId,
          toContact,
          ccContacts,
          overrides,
        });
      } else if (type === ProviderType.Warehouse) {
        await sendWarehouseQuotingEmail({
          requestId,
          quoteId,
          senderContactId,
          toContact,
          ccContacts,
          overrides,
        });
      }
    };

  const onValid = (providerType: ProviderType) => async (data: ProviderQuotingSection) => {
    try {
      const providers = data[providerType].providers.filter((p) => !!p.id) as (ProviderQuotingValues['providers'][0] & {
        id: string;
      })[];
      const message = await sendEmailsToValidProviders(data[providerType], providers, sendSingleEmail(providerType));
      toast({ title: 'Success', description: message, status: 'success' });
    } catch (error) {
      toast({ title: 'Error', description: error?.message, status: 'error' });
    }
  };

  const onError = (errors: any) => {
    console.log('onError', errors);
  };

  const handleSubmit = (providerType: ProviderType) => methods.handleSubmit(onValid(providerType), onError);

  const handleClose = () => {
    methods.reset();
    onClose();
  };

  const defaultTags = useMemo(() => {
    const tags = [ContactTags.Sales];
    if (request?.cargo_type === CargoType.Perishable) {
      tags.push(ContactTags.TempControl);
    }

    return tags;
  }, [request?.cargo_type]);

  return (
    <SimpleForm {...methods} submitOnBlur={false}>
      <form onSubmit={() => {}}>
        <EmailDrawer isOpen={isOpen} onClose={handleClose}>
          <EmailDrawerLeftPanel>
            <Show if={hasAirlines}>
              <Stack>
                <Heading as="h3" size="md">
                  Select Airline(s)
                </Heading>
                <Text size="sm" color="gray.500">
                  Each selected airline will get a separate email with the same content.
                </Text>
              </Stack>
              <SimpleFieldArray
                name="airlines.providers"
                canAdd={false}
                canRemove={false}
                ignoreStyles
                form={({ prefix }) => (
                  <SimpleAirlineRecipientForm
                    defaultAccount={request?.account}
                    prefix={prefix}
                    defaultTags={defaultTags}
                    searchPattern="AIRLINE"
                  />
                )}
              />
            </Show>
            <Show if={hasTruckings}>
              <Stack>
                <Heading as="h3" size="md">
                  Select Truck(s)
                </Heading>
                <Text size="sm" color="gray.500">
                  Each selected truck will get a separate email with the same content.
                </Text>
              </Stack>
              <SimpleFieldArray
                name="trucks.providers"
                canAdd={false}
                canRemove={false}
                ignoreStyles
                form={({ prefix }) => (
                  <SimpleTruckingRecipientForm
                    defaultAccount={request?.account}
                    prefix={prefix}
                    defaultTags={defaultTags}
                    searchPattern="AIRLINE"
                  />
                )}
              />
            </Show>
            <Show if={hasWarehouses}>
              <Stack>
                <Heading as="h3" size="md">
                  Select Warehouse(s)
                </Heading>
                <Text size="sm" color="gray.500">
                  Each selected warehouse will get a separate email with the same content.
                </Text>
              </Stack>
              <SimpleFieldArray
                name="warehouses.providers"
                canAdd={false}
                canRemove={false}
                ignoreStyles
                form={({ prefix }) => (
                  <SimpleWarehouseRecipientForm
                    defaultAccount={request?.account}
                    prefix={prefix}
                    defaultTags={defaultTags}
                    searchPattern="AIRLINE"
                  />
                )}
              />
            </Show>
          </EmailDrawerLeftPanel>
          <EmailDrawerRightPanel title="Request Quote">
            <Stack divider={<Divider />} spacing={6}>
              {hasAirlines && (
                <Stack>
                  <Heading size="md">Airline</Heading>
                  <SimpleSharedEmailForm name="airlines" onSubmit={handleSubmit(ProviderType.Airline)} />
                </Stack>
              )}
              {hasTruckings && (
                <Stack>
                  <Heading size="md">Trucking Company</Heading>
                  <SimpleSharedEmailForm name="trucks" onSubmit={handleSubmit(ProviderType.Trucking)} />
                </Stack>
              )}
              {hasWarehouses && (
                <Stack>
                  <Heading size="md">Warehouse</Heading>
                  <SimpleSharedEmailForm
                    name="warehouses"
                    onSubmit={handleSubmit(ProviderType.Warehouse)}
                    hideFields={['origin']}
                  />
                </Stack>
              )}
            </Stack>
          </EmailDrawerRightPanel>
        </EmailDrawer>
      </form>
    </SimpleForm>
  );
};
